import React, {useEffect, useState} from "react";
import {Button, Modal, ModalBody, ModalCloseButton, ModalContent, ModalHeader, ModalOverlay,} from "components-lib";
import {FaFilePdf} from "react-icons/fa6";
import UploadedFileModal from "./UploadedFileModal";
import MyModal from "./ShowModal";
import {API_BASE_URL, API_ENDPOINTS, axiosInstance} from "config/api";

// Utility functions for enhanced functionality
const formatDate = (date) => {
    if (!date) return 'N/A';
    return new Date(date).toLocaleDateString();
};

/**
 * LabTestsModal - Comprehensive Lab Reports Management Modal
 *
 * This modal component provides a complete interface for managing patient laboratory
 * test reports within the consultation workflow. It enables healthcare providers to
 * view, upload, and organize lab reports efficiently.
 *
 * Key Features:
 * - Display all lab reports for a specific patient in tabular format
 * - Upload new lab reports with file type validation
 * - Preview lab reports (both images and PDF files)
 * - Real-time data fetching and state management
 * - Responsive design with proper file type handling
 * - Integration with file upload and preview modals
 *
 * Component Architecture:
 * - Main modal container with patient lab reports table
 * - Upload functionality via ShowModal integration
 * - File preview capabilities via UploadedFileModal
 * - Automatic data refresh after uploads
 * - Support for multiple file formats (PDF, images)
 *
 * Props:
 * @param {string|number} id - Patient ID for fetching lab reports
 * @param {boolean} isOpen - Modal visibility state
 * @param {Function} onClose - Callback to close the modal
 * @param {string} userName - Patient name for display purposes
 *
 * State Management:
 * - labReportData: Array of lab report objects from API
 * - uploadedFile: Currently selected file for preview
 * - showModal: Controls upload modal visibility
 *
 * API Integration:
 * - GET /api/lab/reports?patient_id={id} - Fetch patient lab reports
 * - Integration with upload endpoints via ShowModal
 * - Automatic data refresh after successful uploads
 *
 * File Handling:
 * - PDF files: Displayed with FaFilePdf icon and preview functionality
 * - Image files: Thumbnail preview with click-to-enlarge
 * - URL handling: Support for both relative and absolute file paths
 *
 * Usage:
 * ```jsx
 * <LabTestsModal
 *   id={patientId}
 *   isOpen={isModalOpen}
 *   onClose={() => setIsModalOpen(false)}
 *   userName={patient.name}
 * />
 * ```
 *
 * Related Components:
 * - ShowModal: Handles lab report uploads
 * - UploadedFileModal: Provides file preview functionality
 * - ConsultationToolbar: Parent component that triggers this modal
 *
 * @component
 * @since 1.0.0
 * @author Kifayti Health
 */
const LabTestsModal = ({id, isOpen, onClose, userName}) => {
    const [showModal, setShowModal] = useState(false);
    const [labReportData, setLabReportData] = useState([]);
    const [uploadedFile, setUploadedFile] = useState(null);

    const openModal = () => {
        setShowModal(true);
    };

    const closeModal = (data) => {
        setShowModal(false);
    };

    const openFileModal = (id, fileUrl, comment) => {
        // Use the direct URL from the API response
        setUploadedFile({id, imageUrl: fileUrl, comment});
    };

    const closeFileModal = () => {
        setUploadedFile(null);
    };

    // const deleteLabReport = async (id) => {
    //   const isConfirmed = window.confirm("Are you sure you want to delete this lab report?");
    //   if (isConfirmed) {
    //     try {
    //       // Using axiosInstance instead of axiosInstanceOld
    //       await axiosInstance.delete(`/api/labreport/deleteLabReport/${id}`);
    //       await fetchData();
    //     } catch (error) {
    //       console.error("Error deleting lab report:", error);
    //       alert("Failed to delete lab report. Please try again.");
    //     }
    //   }
    // };

    const fetchData = async () => {
        try {
            // alert(id)
            const response = await axiosInstance.get(`${API_ENDPOINTS.LAB.GET_REPORTS}?patient_id=${id}`);
            setLabReportData(response.data.data);
        } catch (error) {
            console.error("Error fetching lab report data:", error);
        }
    };

    useEffect(() => {
        if (isOpen) {
            fetchData();
        }
    }, [isOpen]);


    return (
        <Modal isOpen={isOpen} onClose={onClose} size="2xl">
            <ModalOverlay className="bg-black/40 "/>
            <ModalContent className="rounded-2xl shadow-2xl overflow-hidden border-none">
                <ModalHeader className="border-b bg-white py-10 px-8">
                    <div className="flex flex-col">
                        <span className="text-xl font-bold text-gray-800">Lab Reports</span>
                        <span className="text-sm font-normal text-gray-500 mt-0.5">Patient: {userName}</span>
                    </div>
                </ModalHeader>
                <ModalCloseButton/>
                <ModalBody>
                    <div className="container gap-10  p-10">
                        <div className="my-4 mx-4 md:mx-10">
                            <div
                                className="manage-roles-container p-4 flex flex-col gap-4 justify-between border-t-4 border-primary">

                                <div className="overflow-x-auto">
                                    <table className="w-full border-collapse">
                                        <thead className="bg-white text-gray-700">
                                        <tr className="border-b-2 border-black">
                                            <th className="py-3 px-4 text-left">Date</th>
                                            <th className="py-3 px-4 text-center">Report Type</th>
                                            <th className="py-3 px-4 text-center">Lab Reports</th>
                                            {/* <th className="py-3 px-4 text-center">Actions</th> */}
                                        </tr>
                                        </thead>
                                        <tbody>
                                        {Array.isArray(labReportData) &&
                                            labReportData.map((labReportsItem, index) => (
                                                <tr
                                                    key={index}
                                                    className={`
                              ${localStorage.getItem("labReportId") === String(labReportsItem.id) ? "bg-green-100" : ""}
                              border-b border-gray-200
                            `}
                                                >
                                                    <td>{formatDate(labReportsItem.Date)}</td>
                                                    <td className="py-3 px-4 text-center">{labReportsItem.Report_Type}</td>
                                                    <td className="flex gap-16 justify-center">
                                                        {labReportsItem.Lab_Report && labReportsItem.Lab_Report.endsWith(".pdf") ? (
                                                            <FaFilePdf
                                                                className="max-w-12 max-h-12  w-full cursor-pointer  text-red-500"
                                                                onClick={() => openFileModal(labReportsItem.id, labReportsItem.Lab_Report)}
                                                            />
                                                        ) : (
                                                            <img
                                                                src={labReportsItem.Lab_Report.startsWith('http') ? labReportsItem.Lab_Report : `${API_BASE_URL}/uploads/${labReportsItem.Lab_Report}`}
                                                                alt="Lab Report"
                                                                className="max-h-12 max-w-12 inline-block"
                                                                style={{cursor: "pointer"}}
                                                                onClick={() => openFileModal(labReportsItem.id, labReportsItem.Lab_Report.startsWith('http') ? labReportsItem.Lab_Report : `${API_BASE_URL}/uploads/${labReportsItem.Lab_Report}`)}
                                                            />
                                                        )}
                                                    </td>
                                                    {/* <td className="py-3 px-4 text-center">
                              <button
                                className="text-red-500"
                                style={{ fontSize: "1.5rem" }}
                                onClick={() => deleteLabReport(labReportsItem.id)}
                              >
                                <BsTrash />
                              </button>
                            </td> */}
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                    {!Array.isArray(labReportData) && (
                                        <div className="text-left italic font-light">No data present</div>
                                    )}
                                </div>
                                <div className="flex  border-b pb-2 mb-16">
                                    <div className="flex flex-col justify-between w-full mt-4">
                                        <Button
                                            className="text-primary bg-gray border-primary"
                                            onClick={openModal}
                                        >
                                            Upload Lab Report
                                        </Button>
                                        {showModal && (
                                            <MyModal
                                                closeModal={closeModal}
                                                user_id={id}
                                                onSuccess={fetchData}
                                            />
                                        )}
                                        {uploadedFile && id && (
                                            <UploadedFileModal
                                                onClose={closeFileModal}
                                                user_id={id}
                                                file_id={uploadedFile.id}
                                                file={uploadedFile}
                                            />
                                        )}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </ModalBody>
            </ModalContent>
        </Modal>
    );
};

export default LabTestsModal;

